import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getEducationalInfo, addSellerEducationalInfo, updateEducationalDetail, deleteSellerEducationInfo } from '../../virtco/services/data-provider';
import { logout } from '../action/logout-action';

interface EducationState {
  educations: any[]; // Define a more specific type if possible
  loading: boolean;
  error: string | null;
}

const initialState: EducationState = {
  educations: [],
  loading: false,
  error: null,
};

// Async Thunks
export const fetchEducations = createAsyncThunk(
  'education/fetchEducations',
  async (_, { rejectWithValue }) => {
    try {
      const response: any = await getEducationalInfo();
      if (response?.data?.statusCode === 200 && response?.data?.data) {
        // Assuming the API returns an array of education objects
        return response.data.data;
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to fetch education details');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch education details');
    }
  }
);

export const addEducation = createAsyncThunk(
  'education/addEducation',
  async (educationData: any, { rejectWithValue }) => {
    try {
      // Send as an array of objects, as required by backend
      const response: any = await addSellerEducationalInfo([educationData]);
      if (response?.data?.statusCode === 200 && response?.data?.data) {
        return response.data.data;
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to add education details');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to add education details');
    }
  }
);

export const updateEducation = createAsyncThunk(
  'education/updateEducation',
  async (educationData: any, { rejectWithValue }) => {
    try {
      // Send as an array of objects, as required by backend
      const response: any = await updateEducationalDetail([educationData]);
      if (response?.data?.statusCode === 200 && response?.data?.data) {
        return educationData;
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to update education details');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update education details');
    }
  }
);

export const deleteEducation = createAsyncThunk(
  'education/deleteEducation',
  async (educationId: string, { rejectWithValue }) => {
    try {
      const response: any = await deleteSellerEducationInfo(educationId);
      if (response?.data?.statusCode === 200) {
        return educationId; // Return the ID of the deleted item
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to delete education entry');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete education entry');
    }
  }
);

const educationSlice = createSlice({
  name: 'education',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Educations
      .addCase(fetchEducations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEducations.fulfilled, (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.educations = action.payload; // Set the educations array
      })
      .addCase(fetchEducations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to fetch education details';
        state.educations = []; // Clear educations on error
      })
      // Add Education
      .addCase(addEducation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addEducation.fulfilled, (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        // Assuming action.payload contains the new education entry/entries
        // If API returns the full list, replace state.educations = action.payload;
        // If API returns just the new entry, add it: state.educations.push(...action.payload);
        // Let's assume it returns the full list for simplicity based on previous component logic
         state.educations = action.payload;
      })
      .addCase(addEducation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to add education details';
      })
       // Update Education
      .addCase(updateEducation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEducation.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        // Assuming action.payload is the updated education object
        const updatedEducation = action.payload;
        const index = state.educations.findIndex(edu => edu.id === updatedEducation.id);
        if (index !== -1) {
          state.educations[index] = updatedEducation;
        } else {
            // If updated item not found, maybe refetch the list
             // console.warn('Updated education not found in state, consider re-fetching');
             // This scenario depends heavily on API response structure
             // For now, we optimistically update if found, otherwise do nothing or trigger a fetch.
             // A safer approach is often to refetch the list on successful update/add if the API doesn't return the complete updated list.
             // Let's stick to the optimistic update for now based on the assumption the payload contains the updated item.
             // Alternatively, if the API returns the *full list* on update success, replace the state: state.educations = action.payload;
        }
      })
      .addCase(updateEducation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to update education details';
      })
      // Delete Education
      .addCase(deleteEducation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteEducation.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.educations = state.educations.filter(edu => edu.id !== action.payload);
      })
      .addCase(deleteEducation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to delete education entry';
      })
      // Handle logout - reset to initial state
      .addCase(logout, () => initialState);
  },
});

export default educationSlice.reducer; 